import type { ReactNode } from 'react'
import { useState } from 'react'
import type { WorkOrder, WorkOrderStatus } from '../../types/work-orders'

interface WorkOrderCardProps {
  workOrder: WorkOrder
  onApprove: (id: string, notes?: string) => Promise<void>
  onReject: (id: string, notes?: string, reason?: string) => Promise<void>
  onClick?: (workOrder: WorkOrder) => void
}

function timeAgo(date: string): string {
  const seconds = Math.floor((Date.now() - new Date(date).getTime()) / 1000)
  if (seconds < 60) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  return `${days}d ago`
}

function statusLabel(status: WorkOrderStatus): string {
  switch (status) {
    case 'pending_approval':
      return 'PENDING'
    case 'approved':
      return 'APPROVED'
    case 'in_progress':
      return 'IN PROGRESS'
    case 'rejected':
      return 'REJECTED'
    case 'completed':
      return 'COMPLETED'
    case 'failed':
      return 'FAILED'
  }
}

function statusClasses(status: WorkOrderStatus): string {
  switch (status) {
    case 'pending_approval':
      return 'bg-swiss-off-white text-swiss-black border border-swiss-gray-200'
    case 'approved':
      return 'bg-swiss-off-white text-swiss-black border border-swiss-gray-200'
    case 'in_progress':
      return 'bg-swiss-black text-swiss-white'
    case 'rejected':
      return 'bg-swiss-off-white text-swiss-red border border-swiss-gray-200'
    case 'completed':
      return 'bg-swiss-off-white text-[#2E7D32] border border-swiss-gray-200'
    case 'failed':
      return 'bg-[#E53935] text-swiss-white'
  }
}

function priorityBadge(priority: string): ReactNode {
  const p = priority?.toLowerCase()
  if (p === 'critical') {
    return (
      <span className="inline-block px-2 py-0.5 text-swiss-caption font-medium uppercase tracking-widest bg-[#E53935] text-swiss-white">
        CRITICAL
      </span>
    )
  }
  if (p === 'high') {
    return (
      <span className="inline-block px-2 py-0.5 text-swiss-caption font-medium uppercase tracking-widest border border-[#E53935] text-[#E53935]">
        HIGH
      </span>
    )
  }
  return (
    <span className="inline-block px-2 py-0.5 text-swiss-caption font-medium uppercase tracking-widest border border-swiss-gray-300 text-swiss-gray-500">
      {(priority || 'normal').toUpperCase()}
    </span>
  )
}

export function WorkOrderCard({ workOrder, onApprove, onReject, onClick }: WorkOrderCardProps) {
  const [showNotes, setShowNotes] = useState<'approve' | 'reject' | null>(null)
  const [notes, setNotes] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const actionId = workOrder._doc_id || workOrder.id

  const handleApprove = async () => {
    if (showNotes !== 'approve') {
      setShowNotes('approve')
      setNotes('')
      return
    }
    setIsSubmitting(true)
    try {
      await onApprove(actionId, notes || undefined)
    } finally {
      setIsSubmitting(false)
      setShowNotes(null)
      setNotes('')
    }
  }

  const handleReject = async () => {
    if (showNotes !== 'reject') {
      setShowNotes('reject')
      setNotes('')
      return
    }
    setIsSubmitting(true)
    try {
      await onReject(actionId, notes || undefined, notes || undefined)
    } finally {
      setIsSubmitting(false)
      setShowNotes(null)
      setNotes('')
    }
  }

  const handleCancel = () => {
    setShowNotes(null)
    setNotes('')
  }

  return (
    <article
      className={`bg-swiss-off-white border border-swiss-gray-200 p-6 mb-3${onClick ? ' cursor-pointer hover:border-swiss-gray-400 transition-colors duration-150' : ''}`}
      onClick={onClick ? () => onClick(workOrder) : undefined}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(workOrder) } } : undefined}
    >
      {/* Top row: priority + from + timestamp + status */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          {priorityBadge(workOrder.priority)}
          <span className="text-swiss-caption text-swiss-gray-500 uppercase tracking-widest">
            From: {workOrder.from}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <time
            className="text-swiss-caption font-swiss-mono text-swiss-gray-500"
            dateTime={workOrder.received_at}
            title={new Date(workOrder.received_at).toLocaleString()}
          >
            {timeAgo(workOrder.received_at)}
          </time>
          <span
            className={`inline-block px-2 py-0.5 text-swiss-caption font-medium uppercase tracking-widest ${statusClasses(workOrder.status)}`}
          >
            {statusLabel(workOrder.status)}
          </span>
        </div>
      </div>

      {/* Subject */}
      <h3 className="text-[15px] font-semibold tracking-tight mb-2">
        {workOrder.subject}
      </h3>

      {/* Body message */}
      {workOrder.body.message && (
        <p className="text-[13px] text-swiss-gray-600 mb-2 line-clamp-3">
          {workOrder.body.message}
        </p>
      )}

      {/* Action line */}
      {workOrder.body.action && (
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-swiss-caption font-medium uppercase tracking-widest text-swiss-gray-500">
            Action:
          </span>
          <span className="text-[13px] text-swiss-black">
            {workOrder.body.action}
          </span>
        </div>
      )}

      {/* File line */}
      {workOrder.body.file && (
        <div className="flex items-baseline gap-2 mb-1">
          <span className="text-swiss-caption font-medium uppercase tracking-widest text-swiss-gray-500">
            File:
          </span>
          <span className="text-[13px] font-swiss-mono text-swiss-black">
            {workOrder.body.file}
          </span>
        </div>
      )}

      {/* In-progress indicator */}
      {workOrder.status === 'in_progress' && (
        <div className="flex items-center gap-2 mt-3 text-[13px] text-swiss-gray-500">
          <span className="inline-block w-3 h-3 border-2 border-swiss-black border-t-transparent animate-spin" />
          Processing...
        </div>
      )}

      {/* Completed result */}
      {workOrder.status === 'completed' && (
        <div className="flex items-start gap-2 mt-3 py-3 border-t border-swiss-gray-200">
          <span className="text-[#2E7D32] font-medium text-[13px]">&#10003;</span>
          <div className="text-[13px] text-swiss-gray-600">
            {workOrder.result
              ? typeof workOrder.result === 'string'
                ? workOrder.result
                : JSON.stringify(workOrder.result, null, 2).slice(0, 200)
              : 'Completed successfully'}
          </div>
        </div>
      )}

      {/* Failed result */}
      {workOrder.status === 'failed' && (
        <div className="flex items-start gap-2 mt-3 py-3 border-t border-swiss-gray-200">
          <span className="text-[#E53935] font-medium text-[13px]">&#10007;</span>
          <div className="text-[13px] text-[#E53935]">
            {workOrder.result?.error || workOrder.result?.message || 'Execution failed'}
          </div>
        </div>
      )}

      {/* Notes if present */}
      {workOrder.notes && (
        <div className="mt-3 py-3 border-t border-swiss-gray-200">
          <span className="text-swiss-caption font-medium uppercase tracking-widest text-swiss-gray-500">
            Notes:
          </span>
          <p className="text-[13px] text-swiss-gray-600 mt-1">{workOrder.notes}</p>
        </div>
      )}

      {/* Action buttons for pending */}
      {workOrder.status === 'pending_approval' && (
        <div className="mt-4 pt-4 border-t border-swiss-gray-200">
          {/* Notes textarea */}
          {showNotes !== null && (
            <div className="mb-3">
              <label htmlFor={`notes-${workOrder.id}`} className="sr-only">
                {showNotes === 'approve' ? 'Approval notes' : 'Rejection reason'}
              </label>
              <textarea
                id={`notes-${workOrder.id}`}
                className="w-full border border-swiss-gray-200 bg-swiss-white p-3 text-[13px] resize-none focus:outline-none focus:border-swiss-black"
                rows={3}
                placeholder={
                  showNotes === 'approve'
                    ? 'Optional approval notes...'
                    : 'Reason for rejection...'
                }
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              onClick={(e) => {
                e.stopPropagation()
                void handleApprove()
              }}
              disabled={isSubmitting}
              className="px-4 py-2 text-[13px] font-medium bg-swiss-black text-swiss-white cursor-pointer transition-colors duration-150 hover:bg-swiss-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={`Approve work order: ${workOrder.subject}`}
            >
              {isSubmitting && showNotes === 'approve'
                ? 'Approving...'
                : showNotes === 'approve'
                  ? 'Confirm Approve'
                  : 'Approve'}
            </button>

            <button
              onClick={(e) => {
                e.stopPropagation()
                void handleReject()
              }}
              disabled={isSubmitting}
              className="px-4 py-2 text-[13px] font-medium border border-swiss-gray-200 text-swiss-black bg-transparent cursor-pointer transition-colors duration-150 hover:border-swiss-black disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={`Reject work order: ${workOrder.subject}`}
            >
              {isSubmitting && showNotes === 'reject'
                ? 'Rejecting...'
                : showNotes === 'reject'
                  ? 'Confirm Reject'
                  : 'Reject'}
            </button>

            {showNotes !== null && (
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleCancel()
                }}
                className="px-4 py-2 text-[13px] text-swiss-gray-500 cursor-pointer transition-colors duration-150 hover:text-swiss-black"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      )}
    </article>
  )
}
